import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { getComplianceLibraryColumns, Compliance, ComplianceArchiveRequest, GetCompliancesRequest } from '../../models/compliances.model';
import { CompliancesLibraryService } from '../../services/compliances-library.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ComplianceTasksTableComponent } from '../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceStateService } from '../../../_shared/services/compliance-state.service';
import { ConfirmationModalService } from '../../../../../_shared/services/modals/confirmation-modal.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-archive',
  standalone: true,
  imports: [
    CommonModule,
    ComplianceTasksTableComponent
  ],
  templateUrl: './archive.component.html',
  styleUrl: './archive.component.scss'
})
export class ArchiveComponent implements OnInit, OnDestroy {

  private complianceService = inject(CompliancesLibraryService);
  private stateService = inject(ComplianceStateService);
  private errorHandlerService = inject(ErrorHandlerService);
  private confirmationModalService = inject(ConfirmationModalService);
  private transloco = inject(TranslocoService);
  private destroy$: Subject<void> = new Subject<void>();
  private cancellation: Subject<void> = new Subject<void>();

  private accountIds: number[] = [];
  private regionId: number | null = null;
  private countryId: number | null = null;
  private searchTerm: string | null = null;
  private complianceId: number | undefined = undefined;

  complianceData: Compliance[] = [];
  isLoading: boolean = true;
  page: number = 1;
  pageSize: number = 10;
  totalPages: number = 0;
  totalCount: number = 0;

  columns = getComplianceLibraryColumns();

  ngOnInit(): void {
    this.stateService.request$
      .pipe(takeUntil(this.destroy$))
      .subscribe(request => {
        this.accountIds = request.accountIds ?? [];
        this.regionId = request.complianceRegion?.type === 'region' ? request.complianceRegion.id : null;
        this.countryId = request.complianceRegion?.type === 'country' ? request.complianceRegion.id : null;
        this.searchTerm = request.searchTerm ?? null;
        this.complianceId = request.complianceId;
        this.page = 1;
        this.loadArchivedDataAsync();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cancellation.next();
    this.cancellation.complete();
  }

  onSearch(term: string): void {
    this.searchTerm = term?.trim() || null;
    this.loadArchivedDataAsync();
  }

  private async loadArchivedDataAsync(): Promise<void> {
    this.cancellation.next();

    try {
      this.isLoading = true;

      const request: GetCompliancesRequest = {
        accountIds: this.accountIds,
        page: this.page,
        pageSize: this.pageSize,
        regionId: this.regionId,
        countryId: this.countryId,
        searchTerm: this.searchTerm ?? '',
        complianceId: this.complianceId
      };

      const response = await this.complianceService.getArchivedCompliancesAsync(request, this.cancellation);

      const result = response.data;
      this.totalCount = result?.totalCount ?? 0;
      this.totalPages = result?.totalPages ?? 0;

      if (this.page === 1) {
        this.complianceData = result?.results ?? [];
      } else {
        this.complianceData = [...this.complianceData, ...(result?.results ?? [])];
      }

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  onScrollDown(): void {
    if (!this.isLoading && this.page < this.totalPages) {
      this.page++;
      this.loadArchivedDataAsync();
    }
  }

  async onRestoreClickAsync(item: Compliance): Promise<void> {
    let confirmed = false;

    await this.confirmationModalService.showConfirmationModal({
      title: this.transloco.translate('compliance.library.confirmations.restore.title'),
      message: this.transloco.translate('compliance.library.confirmations.restore.message'),
      confirmText: this.transloco.translate('compliance.library.confirmations.restore.confirm'),
      declineText: this.transloco.translate('compliance.library.confirmations.restore.decline'),
      onConfirm: async () => {
        const request: ComplianceArchiveRequest = {
          complianceId: item.id,
          accountIds: this.accountIds
        };
        await this.complianceService.restoreComplianceAsync(request);
        confirmed = true;
      }
    });

    if (confirmed) {
      await this.loadArchivedDataAsync();
      this.stateService.refresh();
    }
  }
}